import { useCallback, useMemo, useState } from 'react';

import { useExistingAnalyticalDatasetNames } from './useExistingAnalyticalDatasetNames';
import { useCopyAnalyticalDatasetMutation } from '../lib/api/useCopyAnalyticalDatasetMutation';
import type { CreatedAnalyticalDataset } from '../lib/api/useCreateAnalyticalDatasetMutation';

const NAME_MAX_CHARACTERS = 60;
const COPY_SUFFIX = ' copy';

/** Default copy name "{source} copy", truncating the base so it fits the cap. */
export const buildCopyName = (sourceName: string): string => {
  const base =
    sourceName.length + COPY_SUFFIX.length > NAME_MAX_CHARACTERS
      ? sourceName.slice(0, NAME_MAX_CHARACTERS - COPY_SUFFIX.length)
      : sourceName;
  return `${base}${COPY_SUFFIX}`;
};

interface UseCopyAnalyticalDatasetDialogArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  sourceName: string;
  onClose: () => void;
  /** Fired with the created copy before close so the caller can navigate into it. */
  onCopied: (profile: CreatedAnalyticalDataset) => void;
}

export interface UseCopyAnalyticalDatasetDialogResult {
  name: string;
  onChangeName: (id: string, value: string) => void;
  onSubmit: () => void;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  /** True when the entered name already belongs to another AD in this project. */
  isDuplicateName: boolean;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the copy-analytical-dataset dialog: name entry (pre-filled
 * "{source} copy"), client-side duplicate-name validation, the copy mutation, and
 * the success → navigate handoff. Intl-free so the view owns copy. A duplicate name
 * is blocked inline (so the request never fires); the server check stays as the
 * authoritative guard for the concurrent-create race.
 */
export const useCopyAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  sourceName,
  onClose,
  onCopied,
}: UseCopyAnalyticalDatasetDialogArgs): UseCopyAnalyticalDatasetDialogResult => {
  const [name, setName] = useState(() => buildCopyName(sourceName));
  const { mutate, isPending, error, reset } = useCopyAnalyticalDatasetMutation();
  const existingNames = useExistingAnalyticalDatasetNames(projectId);

  const onChangeName = useCallback((_id: string, value: string) => {
    setName(value);
  }, []);

  const trimmed = name.trim();
  const isDuplicateName = useMemo(
    () => Boolean(trimmed) && existingNames.has(trimmed.toLowerCase()),
    [existingNames, trimmed]
  );

  const onSubmit = useCallback(() => {
    const next = name.trim();
    if (!next || existingNames.has(next.toLowerCase())) return;
    mutate(
      { projectId, analyticalDatasetProfileId, name: next },
      {
        onSuccess: (response) => {
          onCopied(response.data);
          onClose();
        },
      }
    );
  }, [projectId, analyticalDatasetProfileId, name, existingNames, mutate, onCopied, onClose]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isDuplicateName,
    isValidToSubmit: Boolean(trimmed) && !isDuplicateName,
  };
};
